import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import MuiList from '@mui/material/List';
import { modalClasses } from '@mui/material/Modal';
import NavigationLink, { isBookmarkable, isNavigationItemActive } from './NavigationLink';
import { CollapsedListItem, PopoverHeader, SidebarPopover } from './styles';

export const HOVER_CLOSE_DELAY_MS = 150;

const CollapsedMenuItem = ({
    item,
    callback,
    disabled,
    openMenu,
    setOpenMenu,
    activeBranch = true,
    active,
    narrow,
    ...bookmarkProps
}) => {
    const { pathname } = useLocation();
    const open = openMenu?.id === item.id;
    const pinned = open && openMenu.pinned;
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const leftForModalRef = useRef(false);
    const closeTimerRef = useRef();
    const [focusMenu, setFocusMenu] = useState(false);
    const hasChildren = !!item.children?.length && !disabled;
    const renderMenu = !disabled && item.renderMenu;
    const opensMenu = hasChildren || !!renderMenu;
    const isButton = opensMenu || disabled || (!item.to && !item.external);
    const isExternalLink = !isButton && !!item.external;
    const isActive = active ?? (activeBranch && isNavigationItemActive(item, pathname));
    const hasPopoverActions = opensMenu || isBookmarkable(item, bookmarkProps.toggleBookmark);
    const popoverId = `${item.id}-popover`;
    const menuId = `${item.id}-menu`;

    const close = useCallback(
        () => setOpenMenu(current => (current?.id === item.id ? null : current)),
        [item.id, setOpenMenu],
    );
    useEffect(() => close, [close]);
    const closeOutside = event => {
        const target = event.relatedTarget instanceof Node ? event.relatedTarget : null;
        if (!containerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
            close();
        }
    };
    const cancelHoverClose = () => clearTimeout(closeTimerRef.current);
    const closeAfterHover = () => {
        if (pinned) return;
        cancelHoverClose();
        closeTimerRef.current = setTimeout(close, HOVER_CLOSE_DELAY_MS);
    };
    const openPopover = ({ pin = false, moveFocusIntoMenu = false } = {}) => {
        cancelHoverClose();
        setOpenMenu(current => {
            if (pin) return { id: item.id, pinned: true };
            return current?.pinned || current?.id === item.id ? current : { id: item.id, pinned: false };
        });
        setFocusMenu(moveFocusIntoMenu);
    };
    const handleFocus = event => {
        cancelHoverClose();
        if (containerRef.current?.contains(event.relatedTarget)) return;
        if (leftForModalRef.current) {
            leftForModalRef.current = false;
            return;
        }
        openPopover();
    };
    const handleBlur = event => {
        leftForModalRef.current =
            event.relatedTarget instanceof Element && !!event.relatedTarget.closest(`.${modalClasses.root}`);
        if (!pinned || event.relatedTarget) closeOutside(event);
    };
    const closeAndRefocus = () => {
        triggerRef.current?.focus();
        close();
    };
    const handleClick = destination => {
        closeAndRefocus();
        callback?.(destination);
    };
    const handleTriggerClick = event => {
        if (!opensMenu) {
            handleClick(item);
        } else if (pinned) {
            close();
        } else {
            openPopover({ pin: true, moveFocusIntoMenu: !!renderMenu && event.detail === 0 });
        }
    };
    const openMenuOnArrowDown = event => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openPopover({ pin: true, moveFocusIntoMenu: true });
    };

    return (
        <ClickAwayListener onClickAway={close}>
            <div
                ref={containerRef}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        closeAndRefocus();
                        event.stopPropagation();
                    }
                }}
            >
                <CollapsedListItem
                    ref={triggerRef}
                    as={isButton ? 'button' : isExternalLink ? 'a' : Link}
                    type={isButton ? 'button' : undefined}
                    to={!isButton && !isExternalLink ? item.to : undefined}
                    href={isExternalLink ? item.href : undefined}
                    target={isExternalLink ? '_blank' : undefined}
                    rel={isExternalLink ? 'noopener' : undefined}
                    isActive={isActive}
                    isNarrow={narrow}
                    id={item.id}
                    aria-label={item.ariaLabel ?? item.title}
                    aria-current={isActive ? 'page' : undefined}
                    aria-disabled={disabled || undefined}
                    aria-haspopup={renderMenu ? 'menu' : undefined}
                    aria-expanded={hasPopoverActions ? open : undefined}
                    aria-controls={hasPopoverActions && open ? (renderMenu ? menuId : popoverId) : undefined}
                    onKeyDown={renderMenu ? openMenuOnArrowDown : undefined}
                    onMouseEnter={() => openPopover()}
                    onMouseLeave={closeAfterHover}
                    onClick={disabled ? undefined : handleTriggerClick}
                >
                    {item.icon}
                </CollapsedListItem>
                <Popper
                    open={open}
                    anchorEl={containerRef.current}
                    placement="right-start"
                    disablePortal
                    sx={{ zIndex: 1300 }}
                    modifiers={[
                        { name: 'offset', options: { offset: [0, 0] } },
                        { name: 'flip', options: { fallbackPlacements: ['right-end'] } },
                        { name: 'preventOverflow', options: { padding: 8 } },
                    ]}
                    popperOptions={{ strategy: 'fixed' }}
                >
                    <SidebarPopover
                        ref={popoverRef}
                        id={popoverId}
                        tabIndex={-1}
                        onMouseEnter={cancelHoverClose}
                        onMouseLeave={closeAfterHover}
                    >
                        {hasChildren && <PopoverHeader>{item.title}</PopoverHeader>}
                        {renderMenu ? (
                            renderMenu({ id: menuId, onClose: closeAndRefocus, autoFocusItem: focusMenu })
                        ) : (
                            <MuiList component="div" disablePadding>
                                {(hasChildren ? item.children : [item]).map(child => (
                                    <NavigationLink
                                        key={child.to || child.id}
                                        item={{ ...child, id: undefined }}
                                        compact
                                        activeBranch={activeBranch}
                                        disabled={disabled || child.disabled}
                                        callback={handleClick}
                                        {...bookmarkProps}
                                    />
                                ))}
                            </MuiList>
                        )}
                    </SidebarPopover>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

export default CollapsedMenuItem;
